import React, { useContext } from 'react';
import { Plan } from '../../../types';

export interface Agreement {
    creationDate: string;
    edition: number;
    name: string;
    cvr: string;
    number: number;
    setupId: number;
    plan?: Plan;
    subscription?: {
        id: number;
        level: Plan;
        name: string;
    };
    isTrial: boolean;
    isTrialExpired: boolean;
    showGettingStarted: boolean;
    isRegisteredForElectronicReception: boolean;
    isDeveloper: boolean;
    isPaidByAdmin: boolean;
}

export interface Locale {
    country: {
        code: string;
    };
    currency: {
        code: string;
    };
    formatting: {
        date: string;
        number: {
            decimalSeparator: string;
            groupSeparator: string;
        };
    };
    language: {
        id: number;
        code: string;
        name: string;
    };
}

export interface Session {
    features: any;
    modules: any;
    hasVismaConnect: boolean;
    showESignature: boolean;
    vismaConnectPopups: {
        displayCongratsPopup: boolean;
        joinPopup: {
            variant: number;
            daysUntilBlock: Date;
        };
    };
}

export interface User {
    agreement: Agreement;
    creationDate: string;
    email: string;
    extra: {
        intercomProfile: any;
    };
    guid: string;
    id: string;
    isAdministrator: boolean;
    isSuperUser: boolean;
    isBasicUser: boolean;
    hasAccessAllSettings: boolean;
    hasTimeLimitedAdminAccess: boolean;
    isProjectManager: boolean;
    name: string;
    firstName: string;
    lastName: string;
    vismaId?: string;
}

export interface Identity {
    currentAgreement: Agreement;
    locale: Locale;
    session: Session;
    user: User;
}

export interface EContextProps {
    identity?: Identity;
    routes?: any;
    texts?: any;

    refreshRouting: () => void;
}

export const EContext = React.createContext<EContextProps>({
    identity: undefined,
    routes: undefined,
    texts: undefined,
    refreshRouting: () => {},
});

export const withEContext = Component => props => {
    const context = React.useContext<EContextProps>(EContext);
    return <Component {...props} context={context} />;
};

export function useEContext() {
    const context = useContext(EContext);

    if (!context) {
        throw new Error('useContext must be used within a EContextProvider');
    }

    return context;
}

export default EContext.Consumer;
